import { TextInputComposition } from '@codeleap/web'
import { textVariant } from './Text'
import { createStyles } from '@codeleap/styles'
import { StyleRegistry } from '../styles'

const createTextInputVariant = createStyles<TextInputComposition>

export const TextInputStyles = {
  default: createTextInputVariant(theme => ({
    wrapper: {
      ...theme.presets.column,
      width: 'auto',
      ...theme.presets.fullWidth,
    },
    innerWrapper: {
      minHeight: theme.size['xx-large'],
      borderRadius: theme.radius.small,
      ...theme.spacing.paddingHorizontal(1.5),
      ...theme.border({
        color: theme.colors.fieldRegularStrokeDefault,
        width: theme.stroke.small,
      }),
      paddingVertical: theme.spacing.value(0),
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      backgroundColor: theme.colors.fieldRegularBgDefault,
      cursor: 'text',
    },
    'innerWrapper:hasMultipleLines': {
      minHeight: theme.size['xx-large'] * 2,
      ...theme.spacing.paddingVertical(2),
    },
    'innerWrapper:multiline': {
      ...theme.presets.alignStart,
    },
    selection: {
      color: theme.baseColors.neutralSolid1000,
      backgroundColor: theme.baseColors.neutralSolid300,
    },
    'innerWrapper:focus': {
      ...theme.border({
        color: theme.colors.fieldRegularStrokeDefault,
        width: theme.stroke.small,
      }),
    },
    'innerWrapper:error': {
      ...theme.border({
        color: theme.colors.fieldNegativeStrokeDefault,
        width: theme.stroke.small,
      }),
    },
    'innerWrapper:disabled': {
      ...theme.border({
        color: theme.colors.fieldRegularStrokeDisabled,
        width: theme.stroke.small,
      }),
      cursor: 'not-allowed',
    },
    input: {
      ...textVariant('p3'),
      flex: 1,
      minWidth: null,
      width: '100%',
      color: theme.colors.fieldRegularLabelDefault,
      borderWidth: 0,
    },
    'input:multiline': {
      resize: 'none',
      minHeight: 1.5 * theme.size.large,
      maxHeight: 3 * theme.size.large,
    },
    'input:hasMultipleLines': {
      ...theme.presets.justifyCenter,
    },
    'input:focus': {
      caretColor: theme.baseColors.primarySolid300,
    },
    'input:disabled': {
      color: theme.colors.fieldRegularLabelDisabled,
      backgroundColor: 'transparent',
      cursor: 'not-allowed',
    },
    'placeholder:disabled': {
      color: theme.colors.fieldRegularLabelDefault,
    },
    'input:error': {
      color: theme.colors.fieldNegativeLabelDefault,
    },
    placeholder: {
      color: theme.colors.fieldRegularLabelDefault,
    },
    iconIcon: {
      height: theme.values.iconSize['1.5'],
      width: theme.values.iconSize['1.5'],
      color: theme.colors.fieldRegularIconDefault,
    },
    'iconIcon:focus': {
      color: theme.baseColors.primarySolid300,
    },
    'iconIcon:disabled': {
      color: theme.colors.fieldRegularIconDisabled,
      cursor: 'not-allowed',
    },
    'iconIcon:error': {
      color: theme.colors.fieldNegativeLabelDefault,
    },
    iconTouchableWrapper: {
      ...theme.spacing.padding(0),
      height: 'auto',
      width: 'auto',
      backgroundColor: 'transparent',
      ...theme.border({
        color: theme.colors.buttonHighlightPrimaryBgDefault,
        width: 0,
      }),
    },
    'iconTouchableWrapper:disabled': {
      backgroundColor: 'transparent',
    },
    leftIconTouchableWrapper: {
      ...theme.spacing.marginRight(1),
    },
    rightIconTouchableWrapper: {
      ...theme.spacing.marginLeft(1),
    },
    label: {
      ...textVariant(['c1', 'semiBold']),
      color: theme.colors.textHeadingRegular,
      ...theme.spacing.marginBottom(1),
    },
    description: {
      ...textVariant('p4'),
      olor: theme.colors.textCaption,
      ...theme.spacing.marginBottom(1),
    },
    errorMessage: {
      ...textVariant('p4'),
      color: theme.baseColors.redSolid400,
      ...theme.spacing.marginTop(1),
      ...theme.spacing.paddingLeft(2),
    },
  })),
  line: createTextInputVariant(theme => ({
    innerWrapper: {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.neutralSolid500,
        width: 1,
        directions: ['bottom'],
      }),
      borderRadius: 0,
    },
    'innerWrapper:focus': {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.primarySolid300,
        width: 1,
        directions: ['bottom'],
      }),
    },
    'innerWrapper:error': {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.redSolid400,
        width: 1,
        directions: ['bottom'],
      }),
    },
    'innerWrapper:disabled': {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.neutralSolid200,
        width: 1,
        directions: ['bottom'],
      }),
    },
  })),
  pill: createTextInputVariant(theme => ({
    innerWrapper: {
      borderRadius: theme.radius.infinite,
    },
  })),
  noError: createTextInputVariant(theme => ({
    errorMessage: {
      display: 'none',
    },
  })),
  'radius:small': createTextInputVariant(theme => ({
    innerWrapper: {
      borderRadius: theme.radius.small,
    },
  })),
  'height:largeish': createTextInputVariant(theme => ({
    innerWrapper: {
      minHeight: theme.size.largeish,
    },
  })),
  'bg:default': createTextInputVariant(theme => ({
    innerWrapper: {
      backgroundColor: theme.colors.bgDefault,
    },
  })),
  'border:small': createTextInputVariant(theme => ({
    innerWrapper: {
      ...theme.border({
        color: theme.colors.fieldRegularStrokeDefault,
        width: theme.stroke.small,
      }),
    },
  })),
  'rightIcon:pointer': createTextInputVariant(theme => ({
    rightIconTouchableWrapper: {
      cursor: 'pointer',
    },
  })),
}

StyleRegistry.registerVariants('TextInput', TextInputStyles)
